import { Divider } from "@mantine/core";
import { useNavigate } from "react-router-dom";
import {
  WITHDRAWAL_FLOW_STEPS,
  WITHDRAWAL_TYPE_HEADER,
} from "../config/fundsTransferConfig";
import StepIndicator from "../../ui/StepIndicator/StepIndicator";
import { useWithdrawalFlow } from "../hooks/useWithdrawalFlow";
import { PaymentSummaryCard } from "../../shared/PaymentSummaryCard";
import { CustomLoadingOverlay } from "../../ui/CustomLoadingOverlay/CustomLoadingOverLay";
import { Step1Details } from "../steps/Step1Details";
import { Step2ReviewConfirm } from "../steps/Step2Review";
import { Step3Authorize } from "../steps/Step3Authorize";
import { Step4Result } from "../steps/Step4Result";

function getStepContent({
  currentStep,
  withdrawalType,
  control,
  onNext,
  payoutAccountOptions,
  isAccountsError,
  isAccountsLoading,
  refetchAccounts,
  walletBalance,
  isBalanceError,
  isLoadingCharges,
  isFormLocked,
  selectedPayoutAccount,
  withdrawalAmount,
  watchedSelectedPayoutAccountId,
  transactionFee,
  exciseDuty,
  totalAmount,
  newBalance,
  handleStep2Back,
  handleStep2Confirm,
  isInitiating,
  otpControl,
  handleOtpSubmit,
  otpValue,
  otpSentTo,
  handleStep3Submit,
  isAuthorizing,
  isPolling,
  handleResendOtp,
  resendSecondsLeft,
  isResendCooldownActive,
}) {
  switch (currentStep) {
    case 1:
      return (
        <Step1Details
          withdrawalType={withdrawalType}
          control={control}
          onNext={onNext}
          payoutAccountOptions={payoutAccountOptions}
          isAccountsError={isAccountsError}
          isAccountsLoading={isAccountsLoading}
          refetchAccounts={refetchAccounts}
          walletBalance={walletBalance}
          isBalanceError={isBalanceError}
          isChargesFetching={isLoadingCharges}
          isFormLocked={isFormLocked}
          watchedAmount={withdrawalAmount}
          watchedSelectedPayoutAccountId={watchedSelectedPayoutAccountId}
        />
      );
    case 2:
      return (
        <Step2ReviewConfirm
          withdrawalType={withdrawalType}
          selectedPayoutAccount={selectedPayoutAccount}
          amount={withdrawalAmount}
          transactionFee={transactionFee}
          exciseDuty={exciseDuty}
          totalAmount={totalAmount}
          newBalance={newBalance}
          onBack={handleStep2Back}
          onConfirm={handleStep2Confirm}
          isConfirming={isInitiating}
        />
      );
    case 3:
      return (
        <Step3Authorize
          control={otpControl}
          handleSubmit={handleOtpSubmit}
          otpValue={otpValue}
          otpRecipient={otpSentTo}
          onSubmit={handleStep3Submit}
          isAuthorizing={isAuthorizing || isPolling}
          onResend={handleResendOtp}
          resendSecondsLeft={resendSecondsLeft}
          isResendCooldownActive={isResendCooldownActive}
          submitLabel="Verify & Complete Withdrawal"
        />
      );
    default:
      return null;
  }
}

export function WithdrawalFlowContainer({ withdrawalType }) {
  const navigate = useNavigate();

  const {
    currentStep,
    control,
    payoutAccountOptions,
    isAccountsLoading,
    isAccountsError,
    refetchAccounts,
    walletBalance,
    isBalanceError,
    summaryItems,
    isLoadingCharges,
    isFormLocked,
    selectedPayoutAccount,
    withdrawalAmount,
    watchedSelectedPayoutAccountId,
    transactionFee,
    exciseDuty,
    totalAmount,
    onNext,
    handleStep2Back,
    handleStep2Confirm,
    isInitiating,
    otpControl,
    handleOtpSubmit,
    otpValue,
    otpSentTo,
    handleStep3Submit,
    isAuthorizing,
    handleResendOtp,
    resendSecondsLeft,
    isResendCooldownActive,
    isPolling,
    transactionId,
    remarks,
    status,
    createdDate,
    newBalance,
    isLoadingDisbursement,
    isDisbursementError,
    refetchDisbursement,
  } = useWithdrawalFlow(withdrawalType);

  if (currentStep === 4) {
    return (
      <Step4Result
        transactionId={transactionId}
        status={status}
        createdDate={createdDate}
        amount={withdrawalAmount}
        transactionFee={transactionFee}
        totalDeducted={totalAmount}
        newBalance={newBalance}
        remarks={remarks}
        isLoadingDisbursement={isLoadingDisbursement}
        isDisbursementError={isDisbursementError}
        refetchDisbursement={refetchDisbursement}
        onNewWithdrawal={() => navigate("/funds-transfer")}
        onViewTransactions={() => navigate("/transaction-page")}
      />
    );
  }

  const { title, description } = WITHDRAWAL_TYPE_HEADER[withdrawalType] ?? {};

  const stepContent = getStepContent({
    currentStep,
    withdrawalType,
    control,
    onNext,
    payoutAccountOptions,
    isAccountsError,
    isAccountsLoading,
    refetchAccounts,
    walletBalance,
    isBalanceError,
    isLoadingCharges,
    isFormLocked,
    selectedPayoutAccount,
    withdrawalAmount,
    watchedSelectedPayoutAccountId,
    transactionFee,
    exciseDuty,
    totalAmount,
    newBalance,
    handleStep2Back,
    handleStep2Confirm,
    isInitiating,
    otpControl,
    handleOtpSubmit,
    otpValue,
    otpSentTo,
    handleStep3Submit,
    isAuthorizing,
    isPolling,
    handleResendOtp,
    resendSecondsLeft,
    isResendCooldownActive,
  });

  return (
    <div className="relative flex flex-col md:flex-row gap-6 mt-4">
      <CustomLoadingOverlay
        fullScreen
        visible={isPolling}
        overlayProps={{ blur: 1.4, backgroundOpacity: 0.15 }}
        loaderProps={{ color: "primary", type: "bars" }}
      />
      <div className="flex-1 bg-white rounded-xl border border-gray-200 shadow-sm p-6 sm:p-8">
        <div>
          <h2 className="text-base sm:text-lg font-semibold text-gray-900">
            {title}
          </h2>
          <p className="text-xs sm:text-sm text-gray-500 mt-0.5">
            {description}
          </p>
        </div>

        <Divider className="my-4" />

        <StepIndicator
          steps={WITHDRAWAL_FLOW_STEPS}
          layout="horizontal"
          currentStep={currentStep}
        />

        <Divider className="my-4" />

        {stepContent}
      </div>

      {currentStep < 3 && (
        <PaymentSummaryCard
          title="Withdrawal Summary"
          summaryItems={summaryItems}
        />
      )}
    </div>
  );
}
